import { t } from "../i18n/index.js";
import type { PlayerStat } from "../game/matchStats.js";
import "./resultSummary.css";

/**
 * Lo que TÚ has hecho, y la rejilla que reparte el resumen en dos columnas.
 *
 * Tu propia línea solo aparecía si resultabas ser el MVP: el resto de partidos,
 * el jugador no veía sus números en ningún sitio del final de partido. Y todo
 * lo que sí se enseñaba (MVP, goles, media y XP, trofeos) vivía apilado en una
 * columna de 720 px, por debajo de una cabecera que se comía el 40% del alto.
 *
 * La columna derecha la llena `careerRewardHtml`, que ya trae la media, el
 * nivel, la barra de XP y los trofeos. Aquí solo se le da su sitio.
 *
 * Se devuelve como HTML, igual que `mvpBlockHtml`: `matchChrome` no tiene que
 * conocer el modelo de estadísticas.
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Una celda de dato. `hint` es el nombre largo, para el `title`. */
function tile(value: string | number, label: string, hint: string, strong = false): string {
  return `
    <div class="rs-tile${strong ? " is-strong" : ""}" title="${escapeHtml(hint)}">
      <b>${value}</b>
      <span>${escapeHtml(label)}</span>
    </div>`;
}

/**
 * TU PARTIDO: la fila del humano, en grande y lo primero de todo.
 *
 * Los mismos números que la tabla de estadísticas, pero sin tener que buscarse
 * a uno mismo entre veintidós filas. Es el dato que el jugador viene a mirar y
 * hasta ahora era el único que no estaba destacado en ningún sitio.
 */
export function yourMatchHtml(
  stat: PlayerStat | null,
  isMvp: boolean,
  mvpReason?: string,
): string {
  if (!stat) return "";
  // Precisión de pase: se omite si no ha dado ninguno. Un "0%" cuando no has
  // tocado el balón se lee como un suspenso, y no lo es.
  const accuracy = stat.passes > 0 ? Math.round((stat.passesOk / stat.passes) * 100) : null;

  return `
    <section class="rs-block rs-you">
      <header class="rs-block-head">
        <span>${t("TU PARTIDO")}${isMvp && mvpReason ? ` · ${escapeHtml(mvpReason)}` : ""}</span>
        ${isMvp ? `<em class="rs-mvp-flag">${t("MVP")}</em>` : ""}
      </header>
      <div class="rs-tiles">
        ${tile(stat.goals, t("GOL"), t("Goles"), stat.goals > 0)}
        ${tile(stat.assists, t("ASI"), t("Asistencias"), stat.assists > 0)}
        ${tile(stat.shots, t("TIR"), t("Tiros"))}
        ${tile(stat.recoveries, t("REC"), t("Recuperaciones"))}
        ${accuracy === null ? "" : tile(`${accuracy}%`, t("PAS"), t("Pases completados"))}
      </div>
    </section>`;
}

/**
 * Envuelve las piezas del resumen en la rejilla de dos columnas.
 *
 * Vive aquí y no en `matchChrome` porque el orden importa y depende del
 * contenido: a la izquierda lo del partido que acaba de pasar (tú, el MVP, los
 * goles) y a la derecha lo que se queda contigo (carrera y logros). En pantalla
 * estrecha se apila en ese mismo orden, que es el de interés decreciente.
 */
export function summaryLayoutHtml(parts: {
  you: string;
  mvp: string;
  progress: string;
  extra: string;
}): string {
  return `
    <div class="rs-grid">
      <div class="rs-col">${parts.you}${parts.mvp}</div>
      <div class="rs-col">${parts.progress}</div>
    </div>
    <div class="rs-extra">${parts.extra}</div>`;
}
